/**
 * Web fonts for embroidery text fonts. Contentful's `embroideryTextFont.designFile` is used directly
 * as a CSS `font-family`, so each entry here is keyed by that exact `designFile` value.
 */
type EmbroideryWebFont = {
  fallback: GenericFontFamily
  /** File name in `public/static/fonts/embroidery/`, e.g. 'NotoSans-Regular.woff2'. */
  file?: `${string}.${keyof typeof fontFormats}`
}

type GenericFontFamily = 'serif' | 'sans-serif' | 'cursive'

const fontFormats = {
  woff2: 'woff2',
  woff: 'woff',
} as const

const embroideryWebFonts: Record<string, EmbroideryWebFont> = {
  'noto-sans': { fallback: 'sans-serif', file: 'NotoSans-Medium.woff2' },
  'roboto-condensed': { fallback: 'sans-serif', file: 'RobotoCondensed-Regular.woff2' },
  'le-jeune': { fallback: 'serif', file: 'LeJeune-MediumItalic.woff2' },
  'superette': { fallback: 'serif', file: 'Superette-Bold.woff2' },
  'obviously': { fallback: 'sans-serif', file: 'Obviously.woff2' },
}

// Bump when replacing a font file to force clients to fetch the new one.
const CACHE_BREAK_V = '202610260000' as const

/**
 * `@font-face` rules for every embroidery font, injected into the document <head>. Browsers only
 * download a font file once text on the page actually uses that family, so declaring them globally
 * is cheap.
 */
export const embroideryFontFaces = Object.entries(embroideryWebFonts)
  .flatMap(([family, { file }]) => {
    if (!file) return []
    const extension = file.slice(file.lastIndexOf('.') + 1) as keyof typeof fontFormats
    return [
      `
      @font-face {
        font-family: '${family}';
        src: url('/static/fonts/embroidery/${file}?v=${CACHE_BREAK_V}') format('${fontFormats[extension]}');
        font-weight: 100 900;
        font-display: swap;
      }`,
    ]
  })
  .join('\n')

/**
 * Converts a `designFile` into a CSS `font-family` value with a generic fallback, e.g.
 * `'Le Jeune', serif`. Unknown values (Helvetica, VarsityScriptJF - existing embroidery fonts)
 * pass through unchanged, since quoting a generic keyword would break it.
 */

//TODO: Apply the same font-family structure to the old fonts (Helvetica, VarsityScriptJF)
// LFE-175
export function toEmbroideryFontFamily(designFile: string | undefined): string | undefined {
  if (!designFile) return designFile
  const font = embroideryWebFonts[designFile]
  return font ? `'${designFile}', ${font.fallback}` : designFile
}
